import IconAnimatedSpinner from 'assets/icons/animated-spinner.svg';
import PetsDeliLogoIcon from 'assets/icons/pets-deli-logo-new.svg';
import cn from 'classnames';
import { useConfig } from 'config/use-config';
import useIsClient from 'hooks/common/use-is-client';
import useNavigationData from 'hooks/navigation/use-navigation-data';
import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';
import NextLink from 'next/link';
import { FC, Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Gtm } from 'tracking';
import Search from '../search';
import Dropdown from './dropdown';
import Link from './link';
import Mobile from './mobile';
import PrimaryLink from './primary-link';
import Separator from './separator';
import { useRootStore } from 'stores/use-root-store';
import { useGetHomeLink } from 'hooks/common/use-get-home-link';

const CustomerLinksAsync = dynamic(() => import('./customer-links'), {
  loading: () => <IconAnimatedSpinner className="text-text-dark-bg-contrast-white h-6 w-6" />,
  ssr: false,
});

const Nav: FC = () => {
  const petType = useRootStore((store) => store.petType);
  const homeLink = useGetHomeLink();
  const { desktop } = useNavigationData();
  const { trustedShopsUrl } = useConfig();
  const [dropdownShadow, setDropdownShadow] = useState(false);
  const { isClient } = useIsClient();

  return (
    <>
      <div className="bg-other-white lg:bg-primary-main h-18 lg:h-16">
        <div className="relative container flex h-18 items-center justify-between lg:h-16">
          <div className="flex cursor-pointer items-center justify-center gap-2 lg:hidden">
            <Mobile />

            {isClient && (
              <NextLink
                href={homeLink}
                passHref
                aria-label="Pets Deli"
                data-test="pets-deli-logo-mobile"
              >
                <PetsDeliLogoIcon className="text-primary-main h-6 w-auto lg:hidden" />
              </NextLink>
            )}
          </div>

          <div className="hidden h-full gap-4 lg:flex lg:flex-1">
            {isClient && (
              <NextLink
                href={homeLink}
                className="flex items-center"
                aria-label="Pets Deli"
                data-test="pets-deli-logo-desktop"
              >
                <PetsDeliLogoIcon className="text-other-white h-8 w-auto" />
              </NextLink>
            )}

            <div className="flex items-center">
              {desktop.map((item) => {
                return (
                  <PrimaryLink
                    key={item.title}
                    petContextAware={item.context}
                    href={item.url}
                    title={item.title}
                    onClick={() => {
                      Gtm.track.selectElement({
                        element: ['Navigation', item.title].map(String),
                      });
                    }}
                  >
                    <FormattedMessage id={item.title} />
                  </PrimaryLink>
                );
              })}
            </div>

            <div className="relative mr-3 ml-6 flex h-12 w-87 items-center self-center">
              <Search />
            </div>

            <div className="w-8 self-center">
              <a target="_blank" rel="noopener noreferrer" href={trustedShopsUrl}>
                <div className="h-auto w-8">
                  <Image
                    src="/images/trust/trusted-shops-seal.png"
                    alt="Trusted Shops"
                    layout="responsive"
                    width="144"
                    height="144"
                    role="presentation"
                  />
                </div>
              </a>
            </div>
          </div>

          <div className="flex items-center">
            <CustomerLinksAsync />

            {/* <Suspense
            fallback={<IconAnimatedSpinner className="w-6 h-6 text-text-dark-bg-contrast-white" />}
          > */}
            {/* </Suspense> */}
          </div>
        </div>
      </div>
      <div
        className={cn(
          'hidden items-center justify-between lg:relative lg:block lg:h-[54px] lg:w-full'
        )}
      >
        <div className="container">
          {desktop.map((item, topLevelIndex) => {
            if (item.context) {
              return (
                <div
                  key={topLevelIndex}
                  className={cn(
                    'bg-other-white relative hidden h-14 items-center justify-center lg:flex',
                    petType && petType !== item.context ? 'lg:hidden' : null
                  )}
                >
                  <div className="z-50 flex items-center">
                    {item.items?.map((subNav, index) => {
                      return (
                        <div className="flex text-sm" key={index}>
                          {(subNav.itemType === 'nested-card' ||
                            subNav.itemType === 'nested-entry') &&
                            subNav.items.map((subNavItem, subNavIndex) => {
                              return (
                                <div className="inline-flex" key={subNavIndex}>
                                  {(subNavItem.itemType === 'entry' ||
                                    subNavItem.itemType === 'card') && (
                                    <Link
                                      item={subNavItem}
                                      onClick={() => {
                                        Gtm.track.selectElement({
                                          element: ['Navigation', item.title, subNavItem.title].map(
                                            String
                                          ),
                                        });
                                      }}
                                    >
                                      <FormattedMessage id={subNavItem.title} />
                                    </Link>
                                  )}
                                  {(subNavItem.itemType === 'nested-card' ||
                                    subNavItem.itemType === 'nested-entry') && (
                                    <Dropdown
                                      {...subNavItem}
                                      onClick={() => {
                                        Gtm.track.selectElement({
                                          element: ['Navigation', item.title, subNavItem.title].map(
                                            String
                                          ),
                                        });
                                      }}
                                      onItemClick={(title) => {
                                        Gtm.track.selectElement({
                                          element: [
                                            'Navigation',
                                            item.title,
                                            subNavItem.title,
                                            title,
                                          ].map(String),
                                        });
                                      }}
                                      setDropShadow={setDropdownShadow}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          {item.items && item.items.length - 1 !== index && (
                            <Separator layout="line" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return <Fragment key={topLevelIndex} />;
          })}
        </div>
        {dropdownShadow && (
          <div
            className={cn(
              'bg-other-backdrop-overlay absolute top-full left-0 z-10 h-screen w-screen'
            )}
          />
        )}
      </div>
    </>
  );
};

export default Nav;
